import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Observable, from, merge, of, Subject } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import { normalizeAppRoles } from './role-utils';

/** Deny-only external-session boundary; never grants operational staff authority. */
export function isPartnerSession(claims: Record<string, unknown>): boolean {
  return !!claims['externalAccessType'] || normalizeAppRoles([
    ...(Array.isArray(claims['roles']) ? claims['roles'] : []), claims['role'],
  ]).some(role => ['billing_partner', 'credentialing_partner', 'supply_partner'].includes(role));
}

export interface PrivilegedClaimState {
  roles: string[];
  isSuperAdmin: boolean;
  isOrgAdmin: boolean;
}

/**
 * The ONLY client-side role-claim boundary.
 *
 * Operational shells/guards must use users/{uid} through TenantContextService.
 * This service exists only for privileged organization/platform authority
 * where a mutable profile document must never be sufficient by itself.
 */
@Injectable({ providedIn: 'root' })
export class PrivilegedClaimsService {
  private readonly refresh$ = new Subject<void>();

  readonly state$: Observable<PrivilegedClaimState> = merge(
    this.afAuth.authState,
    this.refresh$.pipe(switchMap(() => from(this.afAuth.currentUser)))
  ).pipe(
    switchMap((user) => user ? from(user.getIdTokenResult(true)) : of(null)),
    map((token) => {
      const claims = (token?.claims ?? {}) as Record<string, unknown>;
      const rawRoles = Array.isArray(claims['roles'])
        ? claims['roles']
        : (typeof claims['role'] === 'string' ? [claims['role']] : []);
      const roles = normalizeAppRoles(rawRoles);
      const administrativeRoles = roles.filter((role) =>
        ['super_admin', 'org_admin', 'admin', 'clinical_admin'].includes(role)
      );
      return {
        roles: administrativeRoles,
        isSuperAdmin: roles.includes('super_admin'),
        isOrgAdmin: administrativeRoles.length > 0,
      };
    }),
    distinctUntilChanged((a, b) =>
      a.isSuperAdmin === b.isSuperAdmin &&
      a.isOrgAdmin === b.isOrgAdmin &&
      JSON.stringify(a.roles) === JSON.stringify(b.roles)
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly isSuperAdmin$ = this.state$.pipe(
    map((state) => state.isSuperAdmin),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly isOrgAdmin$ = this.state$.pipe(
    map((state) => state.isOrgAdmin),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(private readonly afAuth: AngularFireAuth) {}

  /**
   * Compare the signed token mirror with the operational profile. This only
   * decides whether to request a server-side claim repair; it grants no access.
   */
  async matchesOperationalIdentity(expectedRoles: string[], expectedOrgId: string): Promise<boolean> {
    const user = await this.afAuth.currentUser;
    if (!user) return false;

    const claims = (await user.getIdTokenResult(false)).claims as Record<string, unknown>;
    const rawRoles = Array.isArray(claims['roles'])
      ? claims['roles']
      : (typeof claims['role'] === 'string' ? [claims['role']] : []);
    const roles = normalizeAppRoles(rawRoles);
    const orgId = typeof claims['orgId'] === 'string' ? claims['orgId'].trim() : '';
    return expectedRoles.every((role) => roles.includes(role)) && orgId === expectedOrgId;
  }

  refresh(): void {
    this.refresh$.next();
  }
}
